/**
 * When a question is on screen — the one rule shared by everything that renders
 * or validates a feedback form.
 *
 * `visibleWhen: { questionCode, equals }` is what makes a two-branch form
 * possible: a like asks one optional question, a dislike asks for the
 * correction. Three places have to agree about it — the renderer that draws the
 * form, the required-check that decides whether Send is allowed, and the server
 * that re-checks on submit (`feedback_submission_service._was_shown`). Two of
 * those are in this repo, so the rule lives in its own module rather than being
 * exported from the renderer: a component file that also exports a helper breaks
 * fast refresh, and a copied rule is a rule that drifts.
 */
import type { FeedbackQuestion } from '../../types/feedback-master.types';

export type AnswerMap = Record<string, { value: unknown; comment?: string }>;

/** A question with `visibleWhen` only shows once the other answer matches. */
export function isVisible(q: FeedbackQuestion, answers: AnswerMap): boolean {
  const rule = q.visibleWhen;
  if (!rule?.questionCode) return true;
  const other = answers[rule.questionCode]?.value;
  const wanted = rule.equals;
  // No value to match against: the rule means "answered at all".
  if (!wanted || !wanted.length) return other !== undefined && other !== null && other !== '';
  const actual = Array.isArray(other) ? other : [other];
  return actual.some((v) => wanted.includes(v as never));
}
